import React, { useState, useEffect, useCallback } from 'react';
import { billPaymentsAPI } from '../services/api';

const BillPayments = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('payments');
  
  // State management for payments
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [categories, setCategories] = useState({});
  const [statuses, setStatuses] = useState({});
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [payees, setPayees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State management for payees
  const [payeesData, setPayeesData] = useState({});
  const [payeesLoading, setPayeesLoading] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState(null);
  
  // Modal state for payments
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCreatePayeeModal, setShowCreatePayeeModal] = useState(false);
  
  // Modal state for payees
  const [showViewPayeeModal, setShowViewPayeeModal] = useState(false);
  const [showEditPayeeModal, setShowEditPayeeModal] = useState(false);
  const [showDeletePayeeModal, setShowDeletePayeeModal] = useState(false);
  
  // Filter state for payments
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    min_amount: '',
    max_amount: '',
    from_date: '',
    to_date: '',
    per_page: 20,
    page: 1
  });

  // Filter state for payees
  const [payeeFilters, setPayeeFilters] = useState({
    search: '',
    category: '',
    is_active: '',
    per_page: 20,
    page: 1
  });

  // Debounced search for payments
  const [searchInput, setSearchInput] = useState('');
  
  // Debounced search for payees
  const [payeeSearchInput, setPayeeSearchInput] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle search debouncing for payments
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  
  // Handle search debouncing for payees
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPayeeFilters(prev => ({ ...prev, search: payeeSearchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [payeeSearchInput]);

  // Load payments when filters change
  useEffect(() => {
    if (activeTab === 'payments') {
      loadPayments();
    }
  }, [filters, activeTab]);
  
  // Load payees when filters change
  useEffect(() => {
    if (activeTab === 'payees') {
      loadPayeesData();
    }
  }, [payeeFilters, activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [statsRes, categoriesRes, statusesRes, usersRes, accountsRes, payeesRes] = await Promise.all([
        billPaymentsAPI.getBillPaymentStatistics(),
        billPaymentsAPI.getCategories(),
        billPaymentsAPI.getStatuses(),
        billPaymentsAPI.getUsers(),
        billPaymentsAPI.getAccounts(),
        billPaymentsAPI.getPayees()
      ]);
      
      setStatistics(statsRes.data);
      setCategories(categoriesRes.data);
      setStatuses(statusesRes.data);
      setUsers(usersRes.data);
      setAccounts(accountsRes.data);
      setPayees(payeesRes.data.data || payeesRes.data); // Handle paginated response
    } catch (err) {
      setError('Failed to load initial data');
      console.error('Initial data loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await billPaymentsAPI.getBillPayments(filters);
      setPayments(response.data);
    } catch (err) {
      setError('Failed to load payments');
      console.error('Payments loading error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadPayeesData = async () => {
    try {
      setPayeesLoading(true);
      const response = await billPaymentsAPI.getPayees(payeeFilters);
      setPayeesData(response.data);
    } catch (err) {
      setError('Failed to load payees');
      console.error('Payees loading error:', err);
    } finally {
      setPayeesLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      status: '',
      category: '',
      min_amount: '',
      max_amount: '',
      from_date: '',
      to_date: '',
      per_page: 20,
      page: 1
    });
  };
  
  const handlePayeeFilterChange = (key, value) => {
    setPayeeFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearPayeeFilters = () => {
    setPayeeSearchInput('');
    setPayeeFilters({
      search: '',
      category: '',
      is_active: '',
      per_page: 20,
      page: 1
    });
  };

  const handleViewPayment = async (paymentId) => {
    try {
      const response = await billPaymentsAPI.getBillPayment(paymentId);
      setSelectedPayment(response.data);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to load payment details');
    }
  };

  const handleEditPayment = async (paymentId) => {
    try {
      const response = await billPaymentsAPI.getBillPayment(paymentId);
      setSelectedPayment(response.data);
      setShowEditModal(true);
    } catch (err) {
      setError('Failed to load payment details');
    }
  };

  const handleCreatePayment = () => {
    setSelectedPayment(null);
    setShowCreateModal(true);
  };

  const handleCreatePayee = () => {
    setShowCreatePayeeModal(true);
  };

  const handleDeletePayment = (payment) => {
    setSelectedPayment(payment);
    setShowDeleteModal(true);
  };

  const handleProcessPayment = (payment) => {
    setSelectedPayment(payment);
    setShowProcessModal(true);
  };

  const handleCancelPayment = (payment) => {
    setSelectedPayment(payment);
    setShowCancelModal(true);
  };

  const executeProcessPayment = async () => {
    try {
      await billPaymentsAPI.processBillPayment(selectedPayment.id);
      await loadPayments();
      await loadInitialData(); // Refresh statistics
      setShowProcessModal(false);
      setSelectedPayment(null);
      setError(null);
    } catch (err) {
      setError('Failed to process payment: ' + (err.response?.data?.message || err.message));
    }
  };

  const executeCancelPayment = async () => {
    try {
      await billPaymentsAPI.cancelBillPayment(selectedPayment.id);
      await loadPayments();
      await loadInitialData(); // Refresh statistics
      setShowCancelModal(false);
      setSelectedPayment(null);
      setError(null);
    } catch (err) {
      setError('Failed to cancel payment: ' + (err.response?.data?.message || err.message));
    }
  };

  const executeDeletePayment = async () => {
    try {
      await billPaymentsAPI.deleteBillPayment(selectedPayment.id);
      await loadPayments();
      await loadInitialData(); // Refresh statistics
      setShowDeleteModal(false);
      setSelectedPayment(null);
      setError(null);
    } catch (err) {
      setError('Failed to delete payment: ' + (err.response?.data?.message || err.message));
    }
  };

  // Payee handlers
  const handleViewPayee = async (payeeId) => {
    try {
      setError(null);
      const response = await billPaymentsAPI.getPayee(payeeId);
      setSelectedPayee(response.data);
      setShowViewPayeeModal(true);
    } catch (err) {
      setError('Failed to load payee details: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditPayee = async (payeeId) => {
    try {
      setError(null);
      const response = await billPaymentsAPI.getPayee(payeeId);
      setSelectedPayee(response.data);
      setShowEditPayeeModal(true);
    } catch (err) {
      setError('Failed to load payee details: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeletePayee = (payee) => {
    setSelectedPayee(payee);
    setShowDeletePayeeModal(true);
  };

  const executeDeletePayee = async () => {
    try {
      setError(null);
      await billPaymentsAPI.deletePayee(selectedPayee.id);
      setShowDeletePayeeModal(false);
      setSelectedPayee(null);
      // Reload payees data after successful deletion
      if (activeTab === 'payees') {
        await loadPayeesData();
      }
    } catch (err) {
      setError('Failed to delete payee: ' + (err.response?.data?.message || err.message));
      setShowDeletePayeeModal(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statuses[status] || status}
      </span>
    );
  };

  if (loading && !payments.data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bill Payments & Payees Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage bill payments and payees across the system
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <span 
              className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
              onClick={() => setError(null)}
            >
              <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
              </svg>
            </span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('payments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Bill Payments
              </button>
              <button
                onClick={() => setActiveTab('payees')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payees'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Payees Management
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'payments' ? (
          <PaymentsTab
            payments={payments}
            statistics={statistics}
            categories={categories}
            statuses={statuses}
            users={users}
            accounts={accounts}
            payees={payees}
            loading={loading}
            filters={filters}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            handleFilterChange={handleFilterChange}
            clearFilters={clearFilters}
            handleViewPayment={handleViewPayment}
            handleEditPayment={handleEditPayment}
            handleCreatePayment={handleCreatePayment}
            handleCreatePayee={handleCreatePayee}
            handleDeletePayment={handleDeletePayment}
            handleProcessPayment={handleProcessPayment}
            handleCancelPayment={handleCancelPayment}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getStatusBadge={getStatusBadge}
          />
        ) : (
          <PayeesTab
            payeesData={payeesData}
            categories={categories}
            users={users}
            loading={payeesLoading}
            filters={payeeFilters}
            searchInput={payeeSearchInput}
            setSearchInput={setPayeeSearchInput}
            handleFilterChange={handlePayeeFilterChange}
            clearFilters={clearPayeeFilters}
            handleViewPayee={handleViewPayee}
            handleEditPayee={handleEditPayee}
            handleCreatePayee={handleCreatePayee}
            handleDeletePayee={handleDeletePayee}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        )}
        
        {/* Modals */}
        {/* View Modal */}
        {showViewModal && selectedPayment && (
          <ViewPaymentModal
            payment={selectedPayment}
            categories={categories}
            statuses={statuses}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getStatusBadge={getStatusBadge}
            onClose={() => setShowViewModal(false)}
            onProcess={executeProcessPayment}
            onCancel={executeCancelPayment}
          />
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateEditPaymentModal
            payment={null}
            users={users}
            accounts={accounts}
            payees={payees}
            categories={categories}
            statuses={statuses}
            onClose={() => setShowCreateModal(false)}
            onSave={async (data) => {
              try {
                await billPaymentsAPI.createBillPayment(data);
                await loadPayments();
                await loadInitialData();
                setShowCreateModal(false);
                setError(null);
              } catch (err) {
                setError('Failed to create payment: ' + (err.response?.data?.message || err.message));
              }
            }}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedPayment && (
          <CreateEditPaymentModal
            payment={selectedPayment}
            users={users}
            accounts={accounts}
            payees={payees}
            categories={categories}
            statuses={statuses}
            onClose={() => setShowEditModal(false)}
            onSave={async (data) => {
              try {
                await billPaymentsAPI.updateBillPayment(selectedPayment.id, data);
                await loadPayments();
                await loadInitialData();
                setShowEditModal(false);
                setSelectedPayment(null);
                setError(null);
              } catch (err) {
                setError('Failed to update payment: ' + (err.response?.data?.message || err.message));
              }
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedPayment && (
          <ConfirmationModal
            title="Delete Payment"
            message={`Are you sure you want to delete payment ${selectedPayment.payment_id || `#${selectedPayment.id}`}? This action cannot be undone.`}
            confirmText="Delete"
            confirmClass="bg-red-600 hover:bg-red-700"
            onConfirm={executeDeletePayment}
            onCancel={() => {
              setShowDeleteModal(false);
              setSelectedPayment(null);
            }}
          />
        )}

        {/* Process Confirmation Modal */}
        {showProcessModal && selectedPayment && (
          <ConfirmationModal
            title="Process Payment"
            message={`Are you sure you want to process payment ${selectedPayment.payment_id || `#${selectedPayment.id}`} for ${formatCurrency(selectedPayment.amount)}?`}
            confirmText="Process Payment"
            confirmClass="bg-green-600 hover:bg-green-700"
            onConfirm={executeProcessPayment}
            onCancel={() => {
              setShowProcessModal(false);
              setSelectedPayment(null);
            }}
          />
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelModal && selectedPayment && (
          <ConfirmationModal
            title="Cancel Payment"
            message={`Are you sure you want to cancel payment ${selectedPayment.payment_id || `#${selectedPayment.id}`}?`}
            confirmText="Cancel Payment"
            confirmClass="bg-orange-600 hover:bg-orange-700"
            onConfirm={executeCancelPayment}
            onCancel={() => {
              setShowCancelModal(false);
              setSelectedPayment(null);
            }}
          />
        )}

        {/* Create Payee Modal */}
        {showCreatePayeeModal && (
          <CreatePayeeModal
            users={users}
            categories={categories}
            onClose={() => setShowCreatePayeeModal(false)}
            onSave={async (data) => {
              try {
                await billPaymentsAPI.createPayee(data);
                await loadInitialData(); // Refresh payees list
                await loadPayeesData(); // Refresh payees data if on payees tab
                setShowCreatePayeeModal(false);
                setError(null);
              } catch (err) {
                setError('Failed to create payee: ' + (err.response?.data?.message || err.message));
              }
            }}
          />
        )}

        {/* View Payee Modal */}
        {showViewPayeeModal && selectedPayee && (
          <ViewPayeeModal
            payee={selectedPayee}
            categories={categories}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            onClose={() => setShowViewPayeeModal(false)}
          />
        )}

        {/* Edit Payee Modal */}
        {showEditPayeeModal && selectedPayee && (
          <CreateEditPayeeModal
            payee={selectedPayee}
            users={users}
            categories={categories}
            onClose={() => setShowEditPayeeModal(false)}
            onSave={async (data) => {
              try {
                setError(null);
                await billPaymentsAPI.updatePayee(selectedPayee.id, data);
                setShowEditPayeeModal(false);
                setSelectedPayee(null);
                // Reload payees data after successful update
                if (activeTab === 'payees') {
                  await loadPayeesData();
                }
              } catch (err) {
                setError('Failed to update payee: ' + (err.response?.data?.message || err.message));
              }
            }}
          />
        )}

        {/* Delete Payee Confirmation Modal */}
        {showDeletePayeeModal && selectedPayee && (
          <ConfirmationModal
            title="Delete Payee"
            message={`Are you sure you want to delete payee ${selectedPayee.display_name || selectedPayee.name}? This action cannot be undone.`}
            confirmText="Delete"
            confirmClass="bg-red-600 hover:bg-red-700"
            onConfirm={executeDeletePayee}
            onCancel={() => {
              setShowDeletePayeeModal(false);
              setSelectedPayee(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Payments Tab Component
const PaymentsTab = ({ 
  payments, statistics, categories, statuses, users, accounts, payees, loading, 
  filters, searchInput, setSearchInput, handleFilterChange, clearFilters,
  handleViewPayment, handleEditPayment, handleCreatePayment, handleCreatePayee,
  handleDeletePayment, handleProcessPayment, handleCancelPayment,
  formatCurrency, formatDate, getStatusBadge
}) => (
  <>
    {/* Statistics Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">📄</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Payments</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {statistics.total_payments || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">⏳</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {statistics.pending_payments || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">✅</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {statistics.completed_payments || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">❌</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {statistics.failed_payments || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                <span className="text-white text-sm font-medium">💰</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Monthly Volume</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {formatCurrency(statistics.total_amount_month || 0)}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Filters */}
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Search</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Payment ID, payee, user..."
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              {Object.entries(statuses).map(([key, value]) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Categories</option>
              {Object.entries(categories).map(([key, value]) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Payments Table */}
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          Bill Payments
          {payments.total && (
            <span className="ml-2 text-sm text-gray-500">
              ({payments.total} total)
            </span>
          )}
        </h3>
        <div className="flex space-x-3">
          <button
            onClick={handleCreatePayee}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Add Payee
          </button>
          <button
            onClick={handleCreatePayment}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Payment
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : payments.data && payments.data.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.data.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">{payment.user.name}</div>
                        <div className="text-gray-400">{payment.user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">{payment.payee.name}</div>
                        <div className="text-gray-400">{categories[payment.payee.category]}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(payment.scheduled_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewPayment(payment.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditPayment(payment.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Payment"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePayment(payment)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Payment"
                        >
                          Delete
                        </button>
                        {payment.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleProcessPayment(payment)}
                              className="text-green-600 hover:text-green-900"
                              title="Process Payment"
                            >
                              Process
                            </button>
                            <button
                              onClick={() => handleCancelPayment(payment)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Cancel Payment"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {payments.last_page > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleFilterChange('page', Math.max(1, payments.current_page - 1))}
                  disabled={payments.current_page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handleFilterChange('page', Math.min(payments.last_page, payments.current_page + 1))}
                  disabled={payments.current_page === payments.last_page}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{payments.from}</span> to{' '}
                    <span className="font-medium">{payments.to}</span> of{' '}
                    <span className="font-medium">{payments.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handleFilterChange('page', Math.max(1, payments.current_page - 1))}
                      disabled={payments.current_page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(5, payments.last_page) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handleFilterChange('page', page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === payments.current_page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handleFilterChange('page', Math.min(payments.last_page, payments.current_page + 1))}
                      disabled={payments.current_page === payments.last_page}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <div className="text-4xl mb-4">📄</div>
            <h3 className="text-lg font-medium">No payments found</h3>
            <p className="text-sm">Try adjusting your filters to see more results.</p>
          </div>
        </div>
      )}
    </div>
  </>
);

// Payees Tab Component
const PayeesTab = ({ 
  payeesData, categories, users, loading, 
  filters, searchInput, setSearchInput, handleFilterChange, clearFilters,
  handleViewPayee, handleEditPayee, handleCreatePayee, handleDeletePayee,
  formatCurrency, formatDate
}) => (
  <>
    {/* Filters */}
    <div className="bg-white shadow rounded-lg mb-8">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Payee Filters</h3>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Search</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Payee name, display name..."
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Categories</option>
              {Object.entries(categories).map(([key, value]) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Statuses</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Payees Table */}
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          Payees Management
          {payeesData.total && (
            <span className="ml-2 text-sm text-gray-500">
              ({payeesData.total} total)
            </span>
          )}
        </h3>
        <button
          onClick={handleCreatePayee}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New Payee
        </button>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : payeesData.data && payeesData.data.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Default Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AutoPay
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payeesData.data.map((payee) => (
                  <tr key={payee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{payee.display_name}</div>
                        <div className="text-gray-500">{payee.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {categories[payee.category] || payee.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payee.user ? payee.user.name : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payee.default_amount ? formatCurrency(payee.default_amount) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        payee.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {payee.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {payee.autopay_enabled ? (
                        <span className="text-green-600">
                          Day {payee.autopay_day || 'N/A'}
                        </span>
                      ) : (
                        <span className="text-gray-400">Disabled</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewPayee(payee.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleEditPayee(payee.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Payee"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePayee(payee)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Payee"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {payeesData.last_page > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handleFilterChange('page', Math.max(1, payeesData.current_page - 1))}
                  disabled={payeesData.current_page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handleFilterChange('page', Math.min(payeesData.last_page, payeesData.current_page + 1))}
                  disabled={payeesData.current_page === payeesData.last_page}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{payeesData.from}</span> to{' '}
                    <span className="font-medium">{payeesData.to}</span> of{' '}
                    <span className="font-medium">{payeesData.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handleFilterChange('page', Math.max(1, payeesData.current_page - 1))}
                      disabled={payeesData.current_page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(5, payeesData.last_page) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handleFilterChange('page', page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === payeesData.current_page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handleFilterChange('page', Math.min(payeesData.last_page, payeesData.current_page + 1))}
                      disabled={payeesData.current_page === payeesData.last_page}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <div className="text-4xl mb-4">👥</div>
            <h3 className="text-lg font-medium">No payees found</h3>
            <p className="text-sm">Try adjusting your filters or create a new payee.</p>
          </div>
        </div>
      )}
    </div>
  </>
);

// View Payment Modal Component
const ViewPaymentModal = ({ payment, categories, statuses, formatCurrency, formatDate, getStatusBadge, onClose, onProcess, onCancel }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
      <div className="mt-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Payment Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Payment ID</label>
            <p className="mt-1 text-sm text-gray-900">{payment.payment_id || `#${payment.id}`}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <p className="mt-1 text-sm text-gray-900">{formatCurrency(payment.amount)}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <div className="mt-1">{getStatusBadge(payment.status)}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">User</label>
            <p className="mt-1 text-sm text-gray-900">{payment.user.name}</p>
            <p className="text-sm text-gray-500">{payment.user.email}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Payee</label>
            <p className="mt-1 text-sm text-gray-900">{payment.payee.name}</p>
            <p className="text-sm text-gray-500">{categories[payment.payee.category]}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Scheduled Date</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(payment.scheduled_date)}</p>
          </div>
          
          {payment.memo && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Memo</label>
              <p className="mt-1 text-sm text-gray-900">{payment.memo}</p>
            </div>
          )}
          
          {payment.status === 'pending' && (
            <div className="flex space-x-3 pt-4">
              <button
                onClick={onProcess}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md"
              >
                Process Payment
              </button>
              <button
                onClick={onCancel}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-md"
              >
                Cancel Payment
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Create/Edit Payment Modal Component
const CreateEditPaymentModal = ({ payment, users, accounts, payees, categories, statuses, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    user_id: payment?.user?.id || '',
    payee_id: payment?.payee?.id || '',
    from_account_id: payment?.from_account?.id || '',
    amount: payment?.amount || '',
    scheduled_date: payment?.scheduled_date?.split('T')[0] || '',
    processed_date: payment?.processed_date?.split('T')[0] || '',
    memo: payment?.memo || '',
    status: payment?.status || 'pending',
    is_recurring: payment?.is_recurring || false,
    recurring_frequency: payment?.recurring_frequency || '',
    recurring_end_date: payment?.recurring_end_date?.split('T')[0] || ''
  });

  const [filteredAccounts, setFilteredAccounts] = useState(accounts);

  useEffect(() => {
    if (formData.user_id) {
      setFilteredAccounts(accounts.filter(account => account.user_id.toString() === formData.user_id.toString()));
    } else {
      setFilteredAccounts(accounts);
    }
  }, [formData.user_id, accounts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      is_recurring: Boolean(formData.is_recurring),
      user_id: parseInt(formData.user_id),
      payee_id: parseInt(formData.payee_id),
      from_account_id: parseInt(formData.from_account_id)
    };
    
    // Remove empty dates
    if (!submitData.processed_date) delete submitData.processed_date;
    if (!submitData.recurring_end_date) delete submitData.recurring_end_date;
    
    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {payment ? 'Edit Payment' : 'Create Payment'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">User *</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">From Account *</label>
                <select
                  value={formData.from_account_id}
                  onChange={(e) => setFormData({ ...formData, from_account_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Account</option>
                  {filteredAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_number} - {account.account_name} (${account.balance})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Payee *</label>
                <select
                  value={formData.payee_id}
                  onChange={(e) => setFormData({ ...formData, payee_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Payee</option>
                  {payees.map(payee => (
                    <option key={payee.id} value={payee.id}>
                      {payee.name} - {categories[payee.category]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  {Object.entries(statuses).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Scheduled Date *</label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {payment && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Processed Date</label>
                    <input
                      type="date"
                      value={formData.processed_date}
                      onChange={(e) => setFormData({ ...formData, processed_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Memo</label>
              <textarea
                value={formData.memo}
                onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                rows={3}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_recurring}
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">Recurring Payment</label>
            </div>

            {formData.is_recurring && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Frequency</label>
                  <select
                    value={formData.recurring_frequency}
                    onChange={(e) => setFormData({ ...formData, recurring_frequency: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select Frequency</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={formData.recurring_end_date}
                    onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
              >
                {payment ? 'Update Payment' : 'Create Payment'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ title, message, confirmText, confirmClass, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
      <div className="mt-3 text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-4">{message}</p>
        <div className="flex space-x-3">
          <button
            onClick={onConfirm}
            className={`flex-1 text-white font-medium py-2 px-4 rounded-md ${confirmClass}`}
          >
            {confirmText}
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-md"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Create Payee Modal Component
const CreatePayeeModal = ({ users, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    name: '',
    display_name: '',
    category: '',
    account_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    default_amount: '',
    is_active: true,
    autopay_enabled: false,
    autopay_day: '',
    autopay_amount: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      user_id: parseInt(formData.user_id),
      default_amount: formData.default_amount ? parseFloat(formData.default_amount) : null,
      autopay_day: formData.autopay_day ? parseInt(formData.autopay_day) : null,
      autopay_amount: formData.autopay_amount ? parseFloat(formData.autopay_amount) : null,
    };
    
    // Remove empty fields
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' || submitData[key] === null) {
        delete submitData[key];
      }
    });
    
    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New Payee</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">User *</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Category</option>
                  {Object.entries(categories).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., City Electric Company"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name *</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  required
                  placeholder="e.g., Electric Bill"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Account Number</label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="Customer account number"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Default Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_amount}
                  onChange={(e) => setFormData({ ...formData, default_amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  maxLength="2"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  placeholder="CA"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="12345"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Active</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.autopay_enabled}
                    onChange={(e) => setFormData({ ...formData, autopay_enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Enable AutoPay</label>
                </div>
              </div>

              {formData.autopay_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">AutoPay Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.autopay_day}
                      onChange={(e) => setFormData({ ...formData, autopay_day: e.target.value })}
                      placeholder="15"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">AutoPay Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.autopay_amount}
                      onChange={(e) => setFormData({ ...formData, autopay_amount: e.target.value })}
                      placeholder="0.00"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
              >
                Create Payee
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// View Payee Modal Component
const ViewPayeeModal = ({ payee, categories, formatCurrency, formatDate, onClose }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
      <div className="mt-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Payee Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Display Name</label>
            <p className="mt-1 text-sm text-gray-900">{payee.display_name}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Company Name</label>
            <p className="mt-1 text-sm text-gray-900">{payee.name}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <p className="mt-1 text-sm text-gray-900">{categories[payee.category] || payee.category}</p>
          </div>
          
          {payee.account_number && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Number</label>
              <p className="mt-1 text-sm text-gray-900">{payee.account_number}</p>
            </div>
          )}
          
          {payee.phone && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <p className="mt-1 text-sm text-gray-900">{payee.phone}</p>
            </div>
          )}
          
          {payee.default_amount && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Default Amount</label>
              <p className="mt-1 text-sm text-gray-900">{formatCurrency(payee.default_amount)}</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              payee.is_active 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {payee.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">AutoPay</label>
            <p className="mt-1 text-sm text-gray-900">
              {payee.autopay_enabled 
                ? `Enabled - Day ${payee.autopay_day || 'N/A'} (${payee.autopay_amount ? formatCurrency(payee.autopay_amount) : 'No amount set'})`
                : 'Disabled'
              }
            </p>
          </div>
          
          {payee.created_at && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Created</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(payee.created_at)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Create/Edit Payee Modal Component (reuse existing CreatePayeeModal)
const CreateEditPayeeModal = ({ payee, users, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    user_id: payee?.user_id || '',
    name: payee?.name || '',
    display_name: payee?.display_name || '',
    category: payee?.category || '',
    account_number: payee?.account_number || '',
    address: payee?.address || '',
    city: payee?.city || '',
    state: payee?.state || '',
    zip_code: payee?.zip_code || '',
    phone: payee?.phone || '',
    default_amount: payee?.default_amount || '',
    is_active: payee?.is_active ?? true,
    autopay_enabled: payee?.autopay_enabled || false,
    autopay_day: payee?.autopay_day || '',
    autopay_amount: payee?.autopay_amount || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      user_id: parseInt(formData.user_id),
      default_amount: formData.default_amount ? parseFloat(formData.default_amount) : null,
      autopay_day: formData.autopay_day ? parseInt(formData.autopay_day) : null,
      autopay_amount: formData.autopay_amount ? parseFloat(formData.autopay_amount) : null,
    };
    
    // Remove empty fields
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' || submitData[key] === null) {
        delete submitData[key];
      }
    });
    
    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {payee ? 'Edit Payee' : 'Create New Payee'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">User *</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select Category</option>
                  {Object.entries(categories).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., City Electric Company"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Display Name *</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  required
                  placeholder="e.g., Electric Bill"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Account Number</label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="Customer account number"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Default Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_amount}
                  onChange={(e) => setFormData({ ...formData, default_amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Street address"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">State</label>
                <input
                  type="text"
                  maxLength="2"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                  placeholder="CA"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="12345"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Active</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.autopay_enabled}
                    onChange={(e) => setFormData({ ...formData, autopay_enabled: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Enable AutoPay</label>
                </div>
              </div>

              {formData.autopay_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">AutoPay Day</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.autopay_day}
                      onChange={(e) => setFormData({ ...formData, autopay_day: e.target.value })}
                      placeholder="15"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">AutoPay Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.autopay_amount}
                      onChange={(e) => setFormData({ ...formData, autopay_amount: e.target.value })}
                      placeholder="0.00"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
              >
                {payee ? 'Update Payee' : 'Create Payee'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 font-medium py-2 px-4 rounded-md"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BillPayments; 